import * as React from 'react';
import { CmdMultipleSelectFilter } from '@commander-services/gui-components';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import { useIntl } from 'react-intl';
import useOutsideClick from '../../../hooks/useOutsideClick';
import useKeyPress from '../../../hooks/useKeyPress';
import { getIsColumnLast } from '../TableHeader/TableHeader';
import {
  ICheckedTableItems,
  IFilterChipsValues,
  IFilterTable,
  IFilterTripsOverview,
  ITripsOverviewColumn,
  ITripsOverviewTableData,
} from '../interfaces';
import { ESCAPE_KEY } from '../constants';
import {
  checkedTableItemsAtom,
  columnsAtom,
  filterForRequestAtom,
  hasMoreAtom,
  offsetAtom,
  rideBookDataAtom,
  rideBookFilterAtom,
  rideItemIdsAtom,
  rideItemTimestampsAtom,
  summaryItemIndexesAtom,
} from '../TripsOverviewState';
import useAnalytics from '../../../hooks/useAnalytics';

interface IMultiSelectFilterProps {
  id: string;
  filterValues: IFilterTable;
  title?: string;
}

function MultiSelectFilter(props: IMultiSelectFilterProps): JSX.Element {
  const { formatMessage: f } = useIntl();
  const columnsTable = useRecoilValue<ITripsOverviewColumn[]>(columnsAtom);
  const [tableActionsFilters, setTableActionsFilters] = useRecoilState(
    rideBookFilterAtom(props.id)
  );

  const setFilterForRequest = useSetRecoilState<IFilterTripsOverview>(filterForRequestAtom);
  const setCheckedTableItems = useSetRecoilState<ICheckedTableItems>(checkedTableItemsAtom);
  const setOffset = useSetRecoilState<number>(offsetAtom);
  const setTableData = useSetRecoilState<ITripsOverviewTableData | null>(rideBookDataAtom);
  const setRideItemIds = useSetRecoilState<string[]>(rideItemIdsAtom);
  const setHasMore = useSetRecoilState<boolean>(hasMoreAtom);
  const clickOutsideRef: React.RefObject<HTMLDivElement> = React.createRef();
  const setRideItemTimestamps = useSetRecoilState<string[]>(rideItemTimestampsAtom);
  const setSummaryItemIndexes = useSetRecoilState<number[]>(summaryItemIndexesAtom);
  const { trackEvent } = useAnalytics();

  useOutsideClick(clickOutsideRef, () => {
    setTableActionsFilters(
      (tableActionFilter) =>
        tableActionFilter && {
          ...tableActionFilter,
          id: props.id,
          isOpen: false,
          isActionChipsOpen: false,
        }
    );
  });

  useKeyPress(ESCAPE_KEY, () => {
    setTableActionsFilters(
      (tableActionFilter) =>
        tableActionFilter && {
          ...tableActionFilter,
          id: props.id,
          isOpen: false,
          isActionChipsOpen: false,
        }
    );
  });

  const handleOnClick = (
    itemsNumber: number,
    value: string | number | boolean | { min: number; max: number } | (string | number)[]
  ) => {
    const items = props.filterValues.values?.filter((i) =>
      Object.values(value).includes(String(i.value))
    );
    const convertedValue = Object.values(value).map((id) => (id === 'null' ? null : id));
    trackEvent(['ride book', 'click', `Ride book open multiselect filter: ${props.id}`]);

    if (itemsNumber > 0) {
      const selectedValues: IFilterChipsValues = {
        item: items.length === 1 ? items[0].item : items.length,
        value: convertedValue,
        title: props.title ? props.title : '',
        type: 'multiSelect',
      };

      const data = {};

      data[props.id] = { ...convertedValue };

      setFilterForRequest((prevFilter) => ({
        ...prevFilter,
        ...data,
      }));
      setOffset(0);
      setTableData(null);
      setRideItemTimestamps([]);
      setSummaryItemIndexes([]);
      setRideItemIds([]);
      setHasMore(true);
      setCheckedTableItems({
        mainCheckbox: false,
        intervals: [],
        intervalsBlacklist: [],
        rideIdBlacklist: {},
        rideIds: {},
        customerId: null,
        counts: {},
      });

      setTableActionsFilters(
        (tableActionFilter) =>
          tableActionFilter && {
            ...tableActionFilter,
            id: props.id,
            isOpen: false,
            isActionChipsOpen: false,
            isFilterActive: true,
            values: { ...selectedValues },
          }
      );
    }
  };

  const getOptions = (options) => {
    const newOptions = options.map((i) => {
      const item = {
        item: i.item,
        value: String(i.value),
      };
      return item;
    });
    return newOptions;
  };

  const convertValue = (value: string[]) => {
    return value.map((i) => String(i));
  };

  return (
    <div
      ref={clickOutsideRef}
      style={{
        position: 'absolute',
        left: getIsColumnLast(columnsTable, props.id) ? '-24px' : '-204px',
        bottom: '-15px',
      }}
      className="table-filter"
    >
      <div className="label open" style={{ textAlign: 'left' }}>
        <div>
          {props.filterValues && props.filterValues.values && (
            <CmdMultipleSelectFilter
              onChangeCallback={handleOnClick}
              options={getOptions(props.filterValues.values)}
              selectButton={f({ id: 'table.apply' })}
              resetButton="Reset"
              searchPlaceholder={f({ id: 'form.search' })}
              chosenText={f({ id: 'form.chosen' })}
              value={
                tableActionsFilters && tableActionsFilters.values
                  ? convertValue(tableActionsFilters.values.value as string[])
                  : []
              }
              width="290px"
              id={props.id}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export default MultiSelectFilter;
